import { DragEvent, useState } from "react";
import styled from "styled-components";
import { API_CALLS } from "../../../../../API/api_calls";
import { Divider } from "../../../../../commonStyles";
import { actions, useAppDispatch, useAppSelector } from "../../../../../redux";
import {
  CREATE_POST_MODAL_WIDTH,
  CREATE_POST_MODAL_WIDTH_WITH_SCHEDULE_POST,
  disableScroll,
  enableScroll,
} from "../../../../../utils";
import { Modal } from "../../../../Common";
import { FlexColumn } from "../../../../Common/Styles";
import { Attachments } from "./Attachments";
import { AvatarAndText } from "./AvatarAndText";
import CreatePostModal from "./CreatePostModal";
import { CreatePostModalProps } from "./CreatePostModal/CreatePostModal";
import {
  MAX_IMAGE_UPLOAD_COUNT,
  uploadFile,
} from "./CreatePostModal/ModalContent/ImageUpload/utils";
import { DefaultPostProps } from "./DefaultPost";

export const DefaultPost = () => {
  const dispatch = useAppDispatch();
  const [postContentOrder, setPostContentOrder] = useState<
    CreatePostModalProps.PostContentTypes[]
  >([]);
  const [pollOpen, setPollOpen] = useState(false);
  const [sliderOpen, setSliderOpen] = useState(false);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [isAudioOpen, setIsAudioOpen] = useState(false);
  const [imageUploadOpen, setImageUploadOpen] = useState(false);
  const [schedulePostOpen, setSchedulePostOpen] = useState(false);
  const { audio_id } = useAppSelector((state) => state.postAudio);
  const { video_id } = useAppSelector((state) => state.postVideo);
  const { id } = useAppSelector((state) => state.postGIF.postGIF);
  const [isMoreImgUploadButtonDisable, setIsMoreImgUploadButtonDisable] =
    useState(false);
  const { uploadedFiles, createPostOpen, post_id } = useAppSelector(
    (state) => state.posts
  );

  const openCreatePostModal = (openWith?: DefaultPostProps.OpenWith) => {
    dispatch(actions.posts.setCreatePostOpen(true));
    disableScroll();
    if (openWith) {
      switch (openWith) {
        case "Poll":
          setPostContentOrder(["poll"]);
          return setPollOpen(true);
        case "Image":
          setPostContentOrder(["image"]);
          return setImageUploadOpen(true);
        case "Slider":
          setPostContentOrder(["slider"]);
          return setSliderOpen(true);
        case "Video":
          setPostContentOrder(["video"]);
          return setIsVideoOpen(true);
        case "Audio":
          setPostContentOrder(["audio"]);
          return setIsAudioOpen(true);
      }
    }
  };

  const onImageDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (!files.length) return;

    if (isAudioOpen && files.length === 1 && files[0].type.includes("audio")) {
      return API_CALLS.POSTAUDIO.uploadAudioToAPost({
        post_id,
        audioFile: files[0],
        content_id: audio_id,
      });
    }

    if (isVideoOpen && files.length === 1 && files[0].type.includes("video")) {
      return API_CALLS.POSTVIDEO.uploadVideoToAPost({
        post_id,
        video: files[0],
        content_id: video_id,
      });
    }

    if (imageUploadOpen) {
      if (
        isMoreImgUploadButtonDisable ||
        uploadedFiles.length === MAX_IMAGE_UPLOAD_COUNT
      )
        return;

      return uploadFile({
        files,
        post_id,
        uploadedImages: uploadedFiles.length,
        setImageUploadOpen,
        setIsMoreImgUploadButtonDisable,
      });
    }
  };

  const resetPostState = () => {
    dispatch(actions.posts.resetCreatePostData());
    dispatch(actions.postAudio.reset());
    dispatch(actions.postVideo.reset());
    setImageUploadOpen(false);
    setPollOpen(false);
    setSliderOpen(false);
    setIsAudioOpen(false);
    setIsVideoOpen(false);
    setPostContentOrder([]);
    enableScroll();
  };

  return (
    <Container id="create-post-container">
      <AvatarAndText openCreatePostModal={openCreatePostModal} />
      <Divider />
      <Attachments openCreatePostModal={openCreatePostModal} />
      {createPostOpen && (
        <Modal
          from="default-post"
          type="Pages"
          onDrop={onImageDrop}
          maxWidth={
            schedulePostOpen
              ? CREATE_POST_MODAL_WIDTH_WITH_SCHEDULE_POST
              : CREATE_POST_MODAL_WIDTH
          }
          setCloseButton={() => {
            resetPostState();
            if (isAudioOpen) {
              API_CALLS.POSTAUDIO.deleteAudioFromAPost({
                post_id,
                audio_id,
              });
            }
            if (isVideoOpen) {
              API_CALLS.POSTVIDEO.deleteVideoFromAPost({
                post_id,
                video_id,
              });
            }
            if (id) {
              API_CALLS.POSTGIF.deleteGIFForAPost({
                post_id: post_id,
                gif_id: id,
              });
            }
          }}
          showCloseButton={!schedulePostOpen}
        >
          <CreatePostModal
            setPollOpen={setPollOpen}
            pollOpen={pollOpen}
            imageUploadOpen={imageUploadOpen}
            setImageUploadOpen={setImageUploadOpen}
            isMoreImgUploadButtonDisable={isMoreImgUploadButtonDisable}
            setIsMoreImgUploadButtonDisable={setIsMoreImgUploadButtonDisable}
            setSliderOpen={setSliderOpen}
            sliderOpen={sliderOpen}
            schedulePostOpen={schedulePostOpen}
            setSchedulePostOpen={setSchedulePostOpen}
            postContentOrder={postContentOrder}
            setPostContentOrder={setPostContentOrder}
            isVideoOpen={isVideoOpen}
            setIsVideoOpen={setIsVideoOpen}
            isAudioOpen={isAudioOpen}
            setIsAudioOpen={setIsAudioOpen}
            resetPostState={resetPostState}
          />
        </Modal>
      )}
    </Container>
  );
};

const Container = styled(FlexColumn)`
  gap: ${({ theme }) => theme.spacing["spacing-4"].value}rem;
  padding: ${({ theme }) => theme.spacing["spacing-5"].value}rem;
  box-shadow: ${({ theme }) => theme.boxShadows["box-shadow-m"]};
  background: ${({ theme }) =>
    theme.Color.elevation.surface["surface-primary"].value};
  border-radius: ${({ theme }) => theme.radius["radius-xs"].value}rem;
`;
